<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFilePreviewQuery } from '../../data/useFilePreviewQuery';
import { useFileManagerPreview } from '../../logic/core/useFileManagerPreview';
import { ModalTransition } from '../modal-transition';
import { Spinner } from '../spinner';
import { FilePreviewFrame } from './sub-components/file-preview-frame';

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const { t } = useI18n();

const { previewItem, isPreviewOpen } = useFileManagerPreview();

// The item comes from the preview state itself, named by whoever opened the modal (the details
// panel's thumbnail, a double-clicked grid row) — this modal never reaches into another surface's
// state to find out what it is showing.
const { data: preview, isLoading, isError } = useFilePreviewQuery(previewItem, isPreviewOpen);

const title = computed(() => previewItem.value?.name ?? t('fileManager.previewModal.defaultTitle'));
</script>

<template>
  <ModalTransition>
    <div class="modal is-active" role="dialog" aria-modal="true">
      <div class="modal-background" @click="emit('close')"></div>

      <div class="modal-card preview-modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title" :title="title">{{ title }}</p>
          <button
            class="delete"
            :aria-label="t('fileManager.previewModal.closeAriaLabel')"
            type="button"
            @click="emit('close')"
          ></button>
        </header>

        <section class="modal-card-body">
          <div v-if="isLoading" class="preview-status">
            <Spinner />
          </div>
          <div v-else-if="isError || !preview" class="preview-status preview-error">
            {{ t('fileManager.previewModal.cannotPreview') }}
          </div>
          <FilePreviewFrame v-else :preview="preview" />
        </section>
      </div>
    </div>
  </ModalTransition>
</template>

<style scoped lang="scss">
/* The open animation that used to live here now lives in ModalTransition (sub-components/
   modal-transition), shared with every other modal in the file manager — it was identical for all of
   them, and this was the only one that had it. */
.preview-modal-card {
  width: 90vw;
  height: 90vh;
  display: flex;
  flex-direction: column;

  .modal-card-head {
    padding: 0.75rem 1.5rem;
    flex: none;
  }

  .modal-card-title {
    font-size: 1.1rem;
  }

  .delete {
    height: 24px;
    width: 24px;
    max-height: 24px;
    max-width: 24px;
  }

  /* Bulma rounds only .modal-card-head (top corners) and .modal-card-foot (bottom) — this modal has
     no foot, so the body was left square-cornered, and the iframe filling it edge-to-edge made that
     obvious. Rounded here with the same variable the head uses, so the two ends stay identical if
     the theme's radius ever changes, and via the same logical-property pair Bulma itself writes.
     overflow: hidden (already needed to contain the frame) is what clips the iframe to the curve. */
  .modal-card-body {
    flex: 1;
    padding: 0;
    display: flex;
    overflow: hidden;
    border-end-start-radius: var(--bulma-modal-card-head-radius);
    border-end-end-radius: var(--bulma-modal-card-head-radius);
  }
}

.preview-status {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  width: 100%;
}

.preview-error {
  color: #6b7280;
  font-size: 0.9375rem;
}
</style>
